import { Box, BoxProps, ButtonNew, ColorOptions, Image, ModBox, Spacer, Text, TextProps } from '@jfs/ui-new'
import { useIsFocused } from '@react-navigation/native'
import { useEffect, useState } from 'react'
import { BhimUpiIcon } from '../../assets/images/svg/BhimUpi'
import { AmazonPayIcon, BhimAppIcon, GooglePayIcon, PaytmIcon, PhonePeIcon } from '../../assets/images/svg/PaymentIcons'
import { EmptyView } from '../../comps/EmptyView'
import { useLazyApiData, useStores } from '../../data/store'
import { convertHexToRGBA, formatAmount } from '../../utils'
import { staticStrings } from '../../utils/string'

const iconList = [BhimAppIcon, GooglePayIcon, PhonePeIcon, PaytmIcon, AmazonPayIcon]

const Loader = ({ state, onRetry, message, show = false, theme }: any) => {
  return <EmptyView state={state} onRetry={onRetry} message={message} show={show} theme={theme} />
}

// Call dynamic QR API and show the QR on desktop
export const UPIInputModalDesktop = ({ amount, show }: any) => {
  const { REACT_APP_INTENT_STATUS_REQUEST_TIMEOUT_IN_SEC = '120' }: any = process?.env ?? {}
  const defaultTimerCount = parseInt(REACT_APP_INTENT_STATUS_REQUEST_TIMEOUT_IN_SEC)
  const { theme, generateQrCode, updateIntentStatusApi, template } = useStores((root) => ({
    theme: root.config.merchantTheme,
    generateQrCode: root.generateQrCode,
    updateIntentStatusApi: root.transaction.updateIntentStatusApi,
    template: root.merchant.template,
  }))

  const isThemedUi = template?.id === '101'
  const isFocussed = useIsFocused()
  const [imgUrl, setImageUrl] = useState(null)
  const [time, setTimeLeft] = useState(-1)
  const [isLoading, setLoading] = useState(true)
  const [generateQrCodeApi, { state: generateQrCodeState, data: generateQrCodeData }] = useLazyApiData(generateQrCode)

  // call qr API
  useEffect(() => {
    if (isFocussed && !imgUrl?.length && show) {
      setLoading(true)
      generateQrCodeApi(amount)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocussed, show])

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout> | any
    if (isFocussed) {
      if (time >= 1 && imgUrl?.length) {
        timeout = setTimeout(() => {
          setTimeLeft((prevValue) => prevValue - 1)
        }, 1000)
      } else {
        updateIntentStatusApi(false)
        clearTimeout(timeout)
      }
    }
    return () => clearTimeout(timeout)
  }, [isFocussed, imgUrl, time, updateIntentStatusApi])

  useEffect(() => {
    if (generateQrCodeState === 'fulfilled') {
      // api success
      setImageUrl(generateQrCodeData.qr)
      setTimeLeft(defaultTimerCount)
      updateIntentStatusApi(true)
      setLoading(false)
    } else if (generateQrCodeState === 'rejected') {
      updateIntentStatusApi(false)
      setTimeLeft(-1)
      setLoading(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [generateQrCodeState])

  const styles = themeStyles(theme)

  const secondsToMinutes = (duration) => {
    let timerMin = (duration / 60).toString()
    let timerSec = (duration % 60).toString()
    let minutes: any = parseInt(timerMin, 10)
    let seconds: any = parseInt(timerSec, 10)

    minutes = minutes < 10 ? '0' + minutes : minutes
    seconds = seconds < 10 ? '0' + seconds : seconds
    return minutes + ':' + seconds
  }

  let timeInMinutes = secondsToMinutes(time)

  if (!show) {
    return null
  }

  // const showEmptyView = generateQrCodeState === 'pending' || generateQrCodeState === 'rejected'
  if (isLoading) {
    return (
      <Loader
        state={generateQrCodeState}
        onRetry={() => generateQrCodeApi(amount)}
        message={generateQrCodeData?.message}
        show={isLoading}
        theme={theme}
      />
    )
  }

  return (
    <Box {...styles.wrapper}>
      <Box maxWidth={220} {...styles.wrapper}>
        <Spacer size={30} direction="vertical" />
        <Text {...styles.caption}>Scan to Pay</Text>
        <Spacer size={6} direction="vertical" />
        <Text {...styles.amount}>₹{formatAmount(amount)}</Text>
        <Spacer size={2} direction="vertical" />
        {/* QR Code */}
        <ModBox {...styles.qrImgcontainer} width={220} height={220} position={'relative'}>
          {imgUrl?.length && (
            <Image height={220} width={220} resizeMode="stretch" source={'data:image/png;base64, ' + imgUrl} />
          )}
          <ModBox
            show={time === 0}
            position={'absolute'}
            top={0}
            left={0}
            right={0}
            bottom={0}
            zIndex={9}
            borderRadius={14}
            bg={convertHexToRGBA('#fff', 90) as ColorOptions}
          />
          <ModBox
            position={'absolute'}
            justifyContent="center"
            show={time === 0}
            width="100%"
            maxWidth={160}
            align="center"
            zIndex={10}>
            <ButtonNew
              borderRadius={24}
              paddingHorizontal={24}
              type={'primary'}
              height={isThemedUi ? 35 : 48}
              color={theme.brandColor}
              onPress={() => generateQrCodeApi(amount)}>
              <Text
                color={'WHITE'}
                fontFamily="JioType-Medium"
                lineHeight={isThemedUi ? 16 : 20}
                fontSize={isThemedUi ? 10 : 14}>
                {staticStrings.regenerateQR}
              </Text>
            </ButtonNew>
          </ModBox>
        </ModBox>
        <Spacer size={2} direction="vertical" />
        <Box ph={18}>
          <Text
            {...styles.caption}
            lineHeight={18}
            textAlign="center"
            color={isThemedUi ? (theme.bodyTextColor as ColorOptions) : styles.caption.color}>
            {staticStrings.selectAnyUpiApp}
          </Text>
        </Box>
        <Spacer size={16} direction="vertical" />
        <Box flexDirection={'row'}>
          {/* UPI app list */}
          {iconList?.map((SvgImageIcon, index) => {
            return (
              <Box marginHorizontal={4} key={index}>
                <SvgImageIcon color={convertHexToRGBA(theme.brandColor, 8)} />
              </Box>
            )
          })}
        </Box>
        <Spacer size={48} direction="vertical" />
        <ModBox align="center" show={time !== -1}>
          <Text {...styles.caption} color={isThemedUi ? (theme.bodyTextColor as ColorOptions) : styles.caption.color}>
            {time === 0 ? staticStrings.qRCodeExpired : staticStrings.qRCodeExpiresIn}
          </Text>
          <Spacer size={6} direction="vertical" />
          <Text
            {...styles.caption}
            type={'headline'}
            fontFamily="JioType-Bold"
            color={time === 0 ? ('#8F8F8F' as ColorOptions) : 'RED'}>
            {timeInMinutes}
          </Text>
        </ModBox>
        <Spacer size={40} direction="vertical" />
        <BhimUpiIcon />
        <Spacer size={isThemedUi ? 40 : 16} direction="vertical" />
      </Box>
    </Box>
  )
}

const themeStyles = (theme: any) => ({
  wrapper: {
    justify: 'center',
    align: 'center',
  } as BoxProps,
  caption: {
    type: 'caption',
    color: '#8F8F8F' as ColorOptions,
    fontFamily: 'JioType-Medium',
  } as TextProps,
  amount: {
    color: theme.brandColor,
    fontFamily: 'JioType-Black',
    type: 'headline',
  } as TextProps,
  iconContainer: {
    w: 32,
    h: 32,
    borderRadius: 16,
    align: 'center',
    justify: 'center',
    backgroundColor: '#f0f5ff' as ColorOptions,
  } as BoxProps,
  qrImgcontainer: {
    align: 'center',
    justify: 'center',
  } as BoxProps,
})
